/**
 * DataStateView — universeller Renderer für DataState<T>.
 *
 * Gibt den richtigen UI-Zweig pro LoadingStatus aus:
 * - loading       → Skeleton
 * - error         → ErrorCard mit Retry
 * - no_data       → EmptyState mit optionalem CTA
 * - stale         → Daten + Hinweis "Letzter Abruf vor X Min." + Retry
 * - success       → Children
 *
 * Damit ist es unmöglich, "–" oder "Lade…" als Roh-Zustand auszuliefern.
 */

import { AlertCircle, Inbox, RefreshCw } from 'lucide-react';
import type { ReactNode } from 'react';

import type { DataState } from '@/types';
import { cn, formatLastFetch } from '@/lib/utils';

interface DataStateViewProps<T> {
  state: DataState<T>;
  onRetry?: () => void;
  loading?: ReactNode;
  empty?: ReactNode | { title: string; description?: string; action?: ReactNode };
  children: (data: T, meta: { isStale: boolean }) => ReactNode;
  className?: string;
}

export function DataStateView<T>({
  state,
  onRetry,
  loading,
  empty,
  children,
  className,
}: DataStateViewProps<T>) {
  if (state.status === 'loading' || state.status === 'idle') {
    return <div className={className}>{loading ?? <DefaultLoadingPlaceholder />}</div>;
  }

  if (state.status === 'error') {
    return (
      <div className={className}>
        <ErrorCard
          message={state.error?.message ?? 'Unbekannter Fehler'}
          retryPossible={state.error?.retry_possible ?? false}
          onRetry={onRetry}
          lastFetch={state.last_successful_fetch_at}
        />
      </div>
    );
  }

  if (state.status === 'no_data') {
    return <div className={className}>{renderEmpty(empty)}</div>;
  }

  // 'success' or 'stale'
  if (state.data !== null && state.data !== undefined) {
    const isStale = state.status === 'stale';
    return (
      <div className={className}>
        {isStale && (
          <StaleBanner
            lastFetch={state.last_successful_fetch_at}
            onRetry={onRetry}
          />
        )}
        {children(state.data, { isStale })}
      </div>
    );
  }

  return null;
}

function DefaultLoadingPlaceholder() {
  return <div className="skeleton h-12 w-full" />;
}

function renderEmpty(empty: DataStateViewProps<unknown>['empty']): ReactNode {
  if (empty && typeof empty === 'object' && 'title' in empty) {
    return (
      <EmptyState
        title={empty.title}
        description={empty.description}
        action={empty.action}
      />
    );
  }
  return empty ?? <EmptyState title="Keine Daten" />;
}

interface ErrorCardProps {
  message: string;
  retryPossible: boolean;
  onRetry?: () => void;
  lastFetch: string | null;
}

function ErrorCard({ message, retryPossible, onRetry, lastFetch }: ErrorCardProps) {
  return (
    <div className="rounded-lg border border-red-900/60 bg-red-950/30 p-4 flex gap-3">
      <AlertCircle className="size-5 text-red-400 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-red-200">Daten konnten nicht geladen werden</div>
        <div className="text-xs text-red-300/80 mt-1">{message}</div>
        {lastFetch && (
          <div className="text-[11px] text-red-300/60 mt-2">{formatLastFetch(lastFetch)}</div>
        )}
        {retryPossible && onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-red-800/50 bg-red-900/30 px-2.5 py-1 text-xs text-red-100 hover:bg-red-900/50 focus-ring"
          >
            <RefreshCw className="size-3" />
            Erneut versuchen
          </button>
        )}
      </div>
    </div>
  );
}

interface StaleBannerProps {
  lastFetch: string | null;
  onRetry?: () => void;
}

function StaleBanner({ lastFetch, onRetry }: StaleBannerProps) {
  return (
    <div className="rounded-md border border-amber-900/60 bg-amber-950/20 px-3 py-1.5 text-xs text-amber-200 flex items-center justify-between gap-3 mb-3">
      <span>Daten könnten veraltet sein. {formatLastFetch(lastFetch)}</span>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center gap-1 rounded border border-amber-800/50 bg-amber-900/30 px-2 py-0.5 hover:bg-amber-900/50 focus-ring"
        >
          <RefreshCw className="size-3" />
          Aktualisieren
        </button>
      )}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
  className,
}: {
  title: string;
  description?: string | undefined;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'rounded-lg border border-zinc-800/80 bg-zinc-900/30 p-8 text-center',
        className
      )}
    >
      <Inbox className="size-8 text-zinc-600 mx-auto mb-3" />
      <div className="text-sm font-medium text-zinc-200">{title}</div>
      {description && <div className="text-xs text-zinc-400 mt-1">{description}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
